import type { JSX } from "solid-js";

/**
 * Theme-aware inline SVG illustrations for empty states.
 *
 * They are decorative (aria-hidden) — the surrounding empty state always
 * carries the real heading/description text. Colors use `currentColor` plus
 * the `text-primary` accent so every theme stays consistent, and the gentle
 * float animation honors `prefers-reduced-motion` via the global CSS guard.
 */

type EmptyVariant = "search" | "bookmarks" | "notes" | "highlights";

interface EmptyIllustrationProps {
  variant: EmptyVariant;
  class?: string;
}

const COMMON_PROPS = {
  width: "96",
  height: "96",
  viewBox: "0 0 96 96",
  fill: "none",
  "aria-hidden": true,
  role: "presentation" as const,
};

function SearchSvg(): JSX.Element {
  return (
    <svg {...COMMON_PROPS}>
      <rect
        x="20"
        y="14"
        width="44"
        height="58"
        rx="6"
        class="fill-muted/40 stroke-border"
        stroke-width="2"
      />
      <line
        x1="28"
        y1="26"
        x2="50"
        y2="26"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <line
        x1="28"
        y1="34"
        x2="56"
        y2="34"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <line
        x1="28"
        y1="42"
        x2="46"
        y2="42"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <circle cx="58" cy="56" r="16" class="fill-card stroke-primary" stroke-width="3" />
      <line
        x1="70"
        y1="68"
        x2="82"
        y2="80"
        class="stroke-primary"
        stroke-width="4"
        stroke-linecap="round"
      />
    </svg>
  );
}

function BookmarksSvg(): JSX.Element {
  return (
    <svg {...COMMON_PROPS}>
      <rect
        x="22"
        y="16"
        width="52"
        height="64"
        rx="6"
        class="fill-muted/40 stroke-border"
        stroke-width="2"
      />
      <path
        d="M40 16h16v28l-8-7-8 7z"
        class="fill-primary/15 stroke-primary"
        stroke-width="2.5"
        stroke-linejoin="round"
      />
      <line
        x1="32"
        y1="60"
        x2="64"
        y2="60"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <line
        x1="32"
        y1="68"
        x2="56"
        y2="68"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  );
}

function NotesSvg(): JSX.Element {
  return (
    <svg {...COMMON_PROPS}>
      <rect
        x="20"
        y="18"
        width="48"
        height="60"
        rx="6"
        class="fill-muted/40 stroke-border"
        stroke-width="2"
      />
      <line
        x1="28"
        y1="32"
        x2="52"
        y2="32"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <line
        x1="28"
        y1="42"
        x2="58"
        y2="42"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <line
        x1="28"
        y1="52"
        x2="46"
        y2="52"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <path
        d="M56 56l20-20 8 8-20 20-10 2z"
        class="fill-primary/15 stroke-primary"
        stroke-width="2.5"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function HighlightsSvg(): JSX.Element {
  return (
    <svg {...COMMON_PROPS}>
      <rect
        x="18"
        y="20"
        width="60"
        height="56"
        rx="6"
        class="fill-muted/40 stroke-border"
        stroke-width="2"
      />
      <rect x="26" y="32" width="44" height="9" rx="2" class="fill-primary/30" />
      <line
        x1="26"
        y1="50"
        x2="66"
        y2="50"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      <rect x="26" y="58" width="30" height="9" rx="2" class="fill-primary/20" />
    </svg>
  );
}

const VARIANTS: Record<EmptyVariant, () => JSX.Element> = {
  search: SearchSvg,
  bookmarks: BookmarksSvg,
  notes: NotesSvg,
  highlights: HighlightsSvg,
};

export function EmptyIllustration(props: EmptyIllustrationProps): JSX.Element {
  return (
    <div class={`text-muted-foreground/70 motion-safe:animate-float ${props.class ?? ""}`}>
      {VARIANTS[props.variant]()}
    </div>
  );
}
